import { useEffect, useState } from "react";
import axios from "axios";
import { Car, ScanLine, Eye, Boxes, ShieldCheck, Sparkles, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALUE_PROPS = [
  {
    icon: Car,
    title: "Vehicle Registry",
    body: "Register every vehicle you own. Mark one stolen and Guard immediately starts working for you.",
  },
  {
    icon: ScanLine,
    title: "Stolen Detection at Scan",
    body: "Any premise checkpoint scanning your plate triggers an instant security alert with GPS — admins, owner, push notification.",
  },
  {
    icon: Eye,
    title: "Public Lookup",
    body: "Anyone can photograph your stolen plate from /lookup. The same recovery loop fires anonymously — citizens become eyes.",
  },
  {
    icon: Boxes,
    title: "Fleet Management",
    body: "Bulk-import dozens of vehicles by CSV. One-click 'all stolen' for hijacking incidents. Built for organisations.",
  },
];

const FAQ = [
  {
    q: "Do I have to pay?",
    a: "ePass is free during the pilot. You only pay if your account is sanctioned for repeated false alerts (₦10,000 fine).",
  },
  {
    q: "Is my data shared?",
    a: "Public lookups never expose owner identity — only make/model/colour. Scanner names are anonymised on stolen-vehicle history.",
  },
  {
    q: "What if my plate is wrongly marked stolen?",
    a: "You can flip the status back any time from the Vehicles tab. Admins are also notified.",
  },
  {
    q: "Can a thief deactivate it?",
    a: "Stolen status is locked behind your magic-link login. Without your email, a thief can't change it.",
  },
];

/**
 * Citizens who haven't subscribed land on /epass and see this hero. Goal: turn
 * "another menu I clicked" into "this is the moment I register my car." We
 * lead with the only number that actually moves a Nigerian car owner — how
 * many vehicles Guard has helped recover this month — pulled live from the
 * public recovery-stats endpoint.
 */
export default function EPassUpsell({ onSubscribe }) {
  const [recovered, setRecovered] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/epass/recovery-stats?days=30`);
        setRecovered(r.data?.recovered_count ?? null);
      } catch {
        setRecovered(null);
      }
    })();
  }, []);

  return (
    <div className="space-y-4" data-testid="epass-upsell">
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 to-black text-white rounded-sm p-6 sm:p-10 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 opacity-5 pointer-events-none">
          <Car className="w-72 h-72" strokeWidth={1.2} />
        </div>
        <div className="relative max-w-2xl">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-white/10 rounded-sm px-2 py-1 mb-4">
            <Sparkles className="w-3 h-3" /> Free during pilot
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-tight mb-3">
            Make your car uninvitable<br />to thieves.
          </h2>
          <p className="text-sm sm:text-base font-body text-gray-300 mb-6 leading-relaxed">
            Register your vehicle once. The moment it&apos;s flagged stolen, every Guard premise scan and every citizen with a phone becomes a tripwire — instantly, with GPS.
          </p>
          {recovered !== null && recovered > 0 && (
            <p className="text-sm font-body text-green-300 mb-4" data-testid="upsell-recovery-stat">
              <strong className="font-bold text-green-100">{recovered.toLocaleString()} vehicle{recovered === 1 ? "" : "s"} recovered</strong> through Guard in the last 30 days.
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={onSubscribe}
              className="bg-white text-black hover:bg-gray-100 rounded-sm font-body font-bold px-6"
              data-testid="upsell-subscribe-cta"
            >
              Register my vehicle <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 hover:text-white bg-transparent rounded-sm font-body"
                  data-testid="upsell-learn-more"
                >
                  Learn more
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="upsell-modal">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
                    Why subscribe to ePass?
                  </DialogTitle>
                  <DialogDescription>
                    A breakdown of what changes the moment your vehicle is registered.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-5 mt-2">
                  <div className="grid sm:grid-cols-2 gap-3">
                    {VALUE_PROPS.map(({ icon: Icon, title, body }) => (
                      <div key={title} className="border border-gray-200 rounded-sm p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4 text-gray-900" />
                          <p className="font-heading text-sm font-bold text-gray-900">{title}</p>
                        </div>
                        <p className="text-xs text-gray-600 font-body leading-relaxed">{body}</p>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-3">Common Questions</p>
                    <ul className="space-y-3" data-testid="upsell-faq">
                      {FAQ.map(({ q, a }) => (
                        <li key={q}>
                          <p className="text-sm font-bold text-gray-900 font-body mb-0.5 flex items-center gap-1">
                            <ChevronRight className="w-3.5 h-3.5 text-gray-400" /> {q}
                          </p>
                          <p className="text-xs text-gray-600 font-body leading-relaxed pl-4">{a}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    onClick={onSubscribe}
                    className="w-full bg-black hover:bg-gray-800 text-white rounded-sm font-body font-bold"
                    data-testid="upsell-modal-subscribe"
                  >
                    <ShieldCheck className="w-4 h-4 mr-1.5" /> Register my vehicle now
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Value-prop strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="upsell-value-props">
        {VALUE_PROPS.map(({ icon: Icon, title, body }) => (
          <div key={title} className="bg-white border border-gray-200 rounded-sm p-4">
            <Icon className="w-5 h-5 text-gray-900 mb-2" />
            <p className="font-heading text-sm font-bold text-gray-900 mb-1">{title}</p>
            <p className="text-xs text-gray-500 font-body leading-relaxed line-clamp-3">{body}</p>
          </div>
        ))}
      </div>

      {/* Final CTA bar */}
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
        <p className="text-sm font-body text-amber-900">
          <strong className="font-bold">Ready in 30 seconds.</strong> Subscribe → add your plate → done. No app install required.
        </p>
        <Button
          onClick={onSubscribe}
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm font-body shrink-0"
          data-testid="upsell-bottom-cta"
        >
          Subscribe now
        </Button>
      </div>
    </div>
  );
}
